import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Building2, Edit2, Trash2, ShieldCheck, Search,
  Users, FileText, ArrowRight, Trophy,
} from "lucide-react";
import api, { fmtMXN } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { REGIMENES } from "@/components/StampCfdiModal";
import { toast } from "sonner";

const ENTITY_TYPES = [
  { value: "gobierno_federal", label: "Gobierno Federal" },
  { value: "gobierno_estatal", label: "Gobierno Estatal" },
  { value: "gobierno_municipal", label: "Gobierno Municipal" },
  { value: "organismo_publico_descentralizado", label: "Organismo Público Descentralizado" },
  { value: "privado_persona_fisica", label: "Privado · Persona Física" },
  { value: "privado_persona_moral", label: "Privado · Persona Moral" },
];

function typeLabel(t) {
  return ENTITY_TYPES.find((x) => x.value === t)?.label || t;
}

const EMPTY = {
  type: "privado_persona_moral",
  name: "",
  short_name: "",
  legal_representative: "",
  legal_representative_title: "",
  rfc: "",
  fiscal_name: "",
  tax_system: "",
  address: "",
  city: "",
  state: "",
  cp: "",
  phone: "",
  email: "",
  default_iva_pct: 16,
  auditable: false,
  audit_entity: "",
  audit_retention_years: 5,
  notes: "",
  contacts: [],
};

const EMPTY_CONTACT = { name: "", title: "", phone: "", email: "", notes: "" };

export default function ContractingEntities() {
  const [items, setItems] = useState([]);
  const [statsMap, setStatsMap] = useState({});
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const syncFacturapi = async (it) => {
    try {
      await api.post(`/contracting-entities/${it.id}/facturapi-sync`);
      toast.success(`${it.name}: cliente dado de alta en Facturapi ✓`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al sincronizar con Facturapi"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracting-entities");
      setItems(data || []);
      // Cargar stats en paralelo (best-effort)
      const pairs = await Promise.all((data || []).map(async (it) => {
        try {
          const s = await api.get(`/contracting-entities/${it.id}/stats`);
          return [it.id, s.data];
        } catch { return [it.id, null]; }
      }));
      const map = {};
      pairs.forEach(([k, v]) => { if (v) map[k] = v; });
      setStatsMap(map);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((it) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (it.name || "").toLowerCase().includes(q)
      || (it.short_name || "").toLowerCase().includes(q)
      || (it.rfc || "").toLowerCase().includes(q);
  });

  const handleSave = async (e) => {
    e.preventDefault();
    // Limpiar contactos vacíos (sin nombre)
    const payload = {
      ...form,
      contacts: (form.contacts || []).filter((c) => c.name && c.name.trim()),
    };
    try {
      if (editingId) {
        await api.patch(`/contracting-entities/${editingId}`, payload);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/contracting-entities", payload);
        toast.success("Cliente creado");
      }
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({ ...EMPTY, ...it, contacts: it.contacts || [] });
    setOpen(true);
  };

  const handleDelete = async (it) => {
    if (!confirm(`¿Eliminar "${it.name}"?`)) return;
    try {
      await api.delete(`/contracting-entities/${it.id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const addContact = () => setForm({ ...form, contacts: [...(form.contacts || []), { ...EMPTY_CONTACT }] });
  const removeContact = (i) => setForm({ ...form, contacts: form.contacts.filter((_, idx) => idx !== i) });
  const updateContact = (i, key, val) => {
    const next = [...form.contacts];
    next[i] = { ...next[i], [key]: val };
    setForm({ ...form, contacts: next });
  };

  return (
    <div data-testid="contracting-entities-page">
      <PageHeader
        subtitle="Catálogo de clientes (gobierno y privados) — vinculados a contratos, proyectos y préstamos"
        title="Clientes"
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/contracting-entities/ranking"
              data-testid="top-clients-link"
              className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"
              style={{ borderColor: "var(--magenta)", color: "var(--magenta)" }}
            >
              <Trophy className="w-4 h-4" /> Top Clientes
            </Link>
            <button
              data-testid="new-entity-btn"
              onClick={() => { setEditingId(null); setForm(EMPTY); setOpen(true); }}
              className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"
            >
              <Plus className="w-4 h-4" /> Nuevo cliente
            </button>
          </div>
        }
      />

      <div className="px-6 lg:px-8 py-6 space-y-4">
        <div className="panel p-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              data-testid="entity-search"
              placeholder="Buscar por nombre, siglas o RFC..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-cyber pl-10 pr-3 py-2 text-sm w-full"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center text-slate-400 py-10">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="panel p-10 text-center text-slate-400">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            No hay clientes registrados. Crea el primero para empezar.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((it) => {
              const stats = statsMap[it.id];
              return (
                <div key={it.id} data-testid={`entity-card-${it.id}`} className="panel p-4 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>
                        {typeLabel(it.type)}
                      </div>
                      <h3 className="font-display font-bold text-sm text-slate-100 truncate mt-1">{it.name}</h3>
                      {it.short_name && (
                        <div className="text-[11px] text-slate-400 mt-0.5">{it.short_name}</div>
                      )}
                    </div>
                    {it.auditable && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-sm shrink-0"
                        style={{ background: "rgba(217,70,239,0.15)", color: "var(--magenta)", border: "1px solid rgba(217,70,239,0.3)" }}>
                        <ShieldCheck className="w-3 h-3" /> AUDITABLE
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 space-y-0.5">
                    {it.rfc && <div><b className="text-slate-500">RFC:</b> {it.rfc}{" "}
                      {it.tax_system && /^\d{5}$/.test(it.cp || "") ? (
                        <span className="text-[10px]" style={{ color: "#86efac" }} data-testid={`cfdi-ok-${it.id}`}>✓ CFDI</span>
                      ) : (
                        <span className="text-[10px]" style={{ color: "#f59e0b" }} data-testid={`cfdi-missing-${it.id}`}>⚠ faltan datos CFDI</span>
                      )}
                      {it.facturapi_customer_id && <span className="text-[10px] ml-1" style={{ color: "#86efac" }}>· Facturapi ✓</span>}
                      {it.tax_system && /^\d{5}$/.test(it.cp || "") && (
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); syncFacturapi(it); }} data-testid={`facturapi-sync-${it.id}`}
                          className="btn-ghost px-1.5 py-0.5 text-[10px] uppercase tracking-widest ml-1" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>
                          {it.facturapi_customer_id ? "Re-sincronizar" : "Alta en Facturapi"}
                        </button>
                      )}</div>}
                    {it.legal_representative && <div><b className="text-slate-500">Rep:</b> {it.legal_representative}</div>}
                    {it.state && <div><b className="text-slate-500">Estado:</b> {it.state}</div>}
                    {(it.contacts || []).length > 0 && (
                      <div className="flex items-center gap-1 mt-1">
                        <Users className="w-3 h-3 text-slate-500" />
                        <span className="text-slate-500">{it.contacts.length} contacto{it.contacts.length !== 1 ? "s" : ""}</span>
                      </div>
                    )}
                  </div>

                  {stats && (
                    <div
                      data-testid={`entity-stats-${it.id}`}
                      className="mt-3 pt-3 border-t grid grid-cols-3 gap-2"
                      style={{ borderColor: "var(--border)" }}
                    >
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Contratos</div>
                        <div className="font-mono font-bold text-sm text-cyan-300">{stats.contracts_count || 0}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Contratado</div>
                        <div className="font-mono font-bold text-xs text-slate-200">{fmtMXN(stats.total_contracted || 0)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Saldo</div>
                        <div
                          className="font-mono font-bold text-xs"
                          style={{ color: (stats.balance || 0) > 0 ? "#fbbf24" : "#94a3b8" }}
                        >
                          {fmtMXN(stats.balance || 0)}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
                    <Link
                      to={`/contracting-entities/${it.id}`}
                      data-testid={`entity-view-${it.id}`}
                      className="btn-ghost px-2.5 py-1.5 text-[10px] uppercase tracking-widest flex items-center gap-1"
                      style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}
                    >
                      Ver detalle <ArrowRight className="w-3 h-3" />
                    </Link>
                    <div className="flex gap-1">
                      <button
                        data-testid={`entity-edit-${it.id}`}
                        onClick={() => openEdit(it)}
                        className="btn-ghost p-1.5 rounded-sm"
                        title="Editar"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`entity-delete-${it.id}`}
                        onClick={() => handleDelete(it)}
                        className="btn-ghost p-1.5 rounded-sm"
                        style={{ color: "#fca5a5" }}
                        title="Eliminar"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }}
          onClick={() => setOpen(false)}
        >
          <form
            onSubmit={handleSave}
            data-testid="entity-form"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl panel-strong max-h-[90vh] flex flex-col"
          >
            <div className="px-6 py-4 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="label-mono">// {editingId ? "EDITAR" : "NUEVO"} CLIENTE</div>
              <h2 className="font-display font-bold text-lg text-slate-100">
                {editingId ? "Editar cliente" : "Nuevo cliente"}
              </h2>
            </div>
            <div className="px-6 py-5 space-y-3 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Tipo">
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="input-cyber px-3 py-2 text-sm w-full"
                  >
                    {ENTITY_TYPES.map((t) => (
                      <option key={t.value} value={t.value} style={{ background: "#0a0f1d" }}>{t.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Nombre / Razón social *">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-cyber px-3 py-2 text-sm w-full" />
                </Field>
                <Field label="Nombre corto / Siglas">
                  <input value={form.short_name || ""} onChange={(e) => setForm({ ...form, short_name: e.target.value })}
                    placeholder="IGIFE, SCT..."
                    className="input-cyber px-3 py-2 text-sm w-full" />
                </Field>
                <Field label="RFC">
                  <input value={form.rfc || ""} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                    className="input-cyber px-3 py-2 text-sm w-full font-mono" data-testid="entity-rfc" />
                </Field>
                <Field label="Representante legal">
                  <input value={form.legal_representative || ""} onChange={(e) => setForm({ ...form, legal_representative: e.target.value })}
                    className="input-cyber px-3 py-2 text-sm w-full" />
                </Field>
                <Field label="Cargo del representante">
                  <input value={form.legal_representative_title || ""} onChange={(e) => setForm({ ...form, legal_representative_title: e.target.value })}
                    placeholder="DIRECTOR GENERAL"
                    className="input-cyber px-3 py-2 text-sm w-full" />
                </Field>
                <Field label="Estado">
                  <input value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value })}
                    className="input-cyber px-3 py-2 text-sm w-full" />
                </Field>
                <Field label="Ciudad">
                  <input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="input-cyber px-3 py-2 text-sm w-full" />
                </Field>
                <Field label="Dirección" wide>
                  <input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="input-cyber px-3 py-2 text-sm w-full" />
                </Field>
                <Field label="% IVA default">
                  <input type="number" step="0.01" min="0" max="100" value={form.default_iva_pct}
                    onChange={(e) => setForm({ ...form, default_iva_pct: parseFloat(e.target.value) || 0 })}
                    className="input-cyber px-3 py-2 text-sm w-full" />
                </Field>
                <Field label="Email institucional">
                  <input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="input-cyber px-3 py-2 text-sm w-full" />
                </Field>
                <Field label="Teléfono institucional">
                  <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="input-cyber px-3 py-2 text-sm w-full" />
                </Field>
              </div>

              {/* Datos de facturación CFDI */}
              <div className="panel mt-2 p-3 space-y-2" data-testid="entity-fiscal-section">
                <div className="label-mono">// DATOS DE FACTURACIÓN (CFDI) — como su Constancia de Situación Fiscal</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Razón social fiscal (sin régimen societario)">
                    <input value={form.fiscal_name || ""} onChange={(e) => setForm({ ...form, fiscal_name: e.target.value.toUpperCase() })}
                      placeholder={form.name ? form.name.toUpperCase() : "COMO APARECE EN LA CONSTANCIA"}
                      className="input-cyber px-3 py-2 text-sm w-full uppercase" data-testid="entity-fiscal-name" />
                  </Field>
                  <Field label="Régimen fiscal SAT">
                    <select value={form.tax_system || ""} onChange={(e) => setForm({ ...form, tax_system: e.target.value })}
                      className="input-cyber px-2 py-2 text-sm w-full" data-testid="entity-tax-system">
                      <option value="" style={{ background: "#0a0f1d" }}>— Selecciona —</option>
                      {REGIMENES.map(([v, l]) => <option key={v} value={v} style={{ background: "#0a0f1d" }}>{l}</option>)}
                    </select>
                  </Field>
                  <Field label="C.P. fiscal (5 dígitos)">
                    <input value={form.cp || ""} onChange={(e) => setForm({ ...form, cp: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                      className="input-cyber px-3 py-2 text-sm w-full font-mono" data-testid="entity-cp" />
                  </Field>
                  <div className="text-[11px] text-slate-500 self-end pb-2">Estos datos se usan al timbrar CFDI de las obras de este cliente (junto con el RFC y correo de arriba).</div>
                </div>
              </div>

              {/* Contactos (multi) */}
              <div className="panel mt-2">
                <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--border)", background: "rgba(15,23,42,0.6)" }}>
                  <div className="label-mono flex items-center gap-2">
                    <Users className="w-3.5 h-3.5" /> CONTACTOS / PERSONAS DE CONTACTO
                  </div>
                  <button type="button" onClick={addContact} data-testid="add-contact-btn"
                    className="btn-ghost px-2.5 py-1 text-[10px] uppercase tracking-widest flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Agregar
                  </button>
                </div>
                <div className="p-3">
                  {(form.contacts || []).length === 0 ? (
                    <div className="text-xs text-slate-500 italic">Sin contactos. Agrega quién atiende este cliente (residente, supervisor de obra, jefe administrativo…).</div>
                  ) : (
                    <div className="space-y-2">
                      {form.contacts.map((c, i) => (
                        <div key={i} data-testid={`contact-row-${i}`} className="grid grid-cols-12 gap-2 items-start">
                          <input value={c.name} onChange={(e) => updateContact(i, "name", e.target.value)}
                            placeholder="Nombre"
                            className="input-cyber px-2.5 py-1.5 text-xs col-span-3" />
                          <input value={c.title || ""} onChange={(e) => updateContact(i, "title", e.target.value)}
                            placeholder="Cargo"
                            className="input-cyber px-2.5 py-1.5 text-xs col-span-3" />
                          <input value={c.phone || ""} onChange={(e) => updateContact(i, "phone", e.target.value)}
                            placeholder="Teléfono"
                            className="input-cyber px-2.5 py-1.5 text-xs col-span-2 font-mono" />
                          <input type="email" value={c.email || ""} onChange={(e) => updateContact(i, "email", e.target.value)}
                            placeholder="Email"
                            className="input-cyber px-2.5 py-1.5 text-xs col-span-3" />
                          <button type="button" onClick={() => removeContact(i)}
                            data-testid={`remove-contact-${i}`}
                            className="btn-ghost p-1.5 rounded-sm col-span-1" style={{ color: "#fca5a5" }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="auditable"
                  checked={form.auditable}
                  onChange={(e) => setForm({ ...form, auditable: e.target.checked })}
                  className="w-4 h-4 accent-magenta-500"
                />
                <label htmlFor="auditable" className="text-sm text-slate-300">
                  Las obras son <b className="text-magenta-300">auditables</b> (fondos federales / estatales)
                </label>
              </div>
              {form.auditable && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Entidad fiscalizadora">
                    <input value={form.audit_entity || ""} onChange={(e) => setForm({ ...form, audit_entity: e.target.value })}
                      placeholder="ASF, ASE, Contraloría..."
                      className="input-cyber px-3 py-2 text-sm w-full" />
                  </Field>
                  <Field label="Años de retención">
                    <input type="number" min="1" max="20" value={form.audit_retention_years || 5}
                      onChange={(e) => setForm({ ...form, audit_retention_years: parseInt(e.target.value, 10) || 5 })}
                      className="input-cyber px-3 py-2 text-sm w-full" />
                  </Field>
                </div>
              )}
              <Field label="Notas">
                <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2} className="input-cyber px-3 py-2 text-sm w-full" />
              </Field>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
              <button type="button" onClick={() => setOpen(false)}
                className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest">Cancelar</button>
              <button type="submit" data-testid="entity-submit"
                className="btn-primary px-4 py-2 text-xs uppercase tracking-widest">
                {editingId ? "Guardar" : "Crear"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <label className="label-mono block mb-1">{label}</label>
      {children}
    </div>
  );
}
